import { Link } from 'react-router-dom';
import {
  Container,
  Title,
  Table,
  Badge,
  Card,
  Text,
  Stack,
  Group,
  Anchor,
  Loader,
  Alert,
  Button,
  Modal,
  Pagination,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconExclamationCircle, IconHeartStar, IconBuildingStore } from '@tabler/icons-react';
import { useState } from 'react';
import { useAuth } from '@clerk/react';
import { useApi, useApiClient } from '../../api';
import ProtectedRoute from '../../components/auth/ProtectedRoute';
import { formatCents } from '../../components/products/productDisplay';

const PAGE_LIMIT = 20;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// FanSubscription statuses (contract §3.17)
const STATUS_META = {
  pending: { color: 'yellow', label: 'Processing' },
  active: { color: 'green', label: 'Active' },
  past_due: { color: 'orange', label: 'Past due' },
  canceled: { color: 'gray', label: 'Canceled' },
  expired: { color: 'gray', label: 'Expired' },
};

// Amended §4.19: a canceled subscription keeps gallery access until
// current_period_end.
const isCanceledWithinPeriod = (sub) =>
  sub.status === 'canceled' &&
  sub.current_period_end &&
  new Date(sub.current_period_end) > new Date();

const StatusBadge = ({ sub }) => {
  if (isCanceledWithinPeriod(sub)) {
    return (
      <Badge color="teal" variant="light">
        Canceled — access until {formatDate(sub.current_period_end)}
      </Badge>
    );
  }
  const meta = STATUS_META[sub.status] || { color: 'gray', label: sub.status };
  return <Badge color={meta.color} variant="light">{meta.label}</Badge>;
};

// One FanSubscription row (contract §3.17). Active subs renew at
// current_period_end; canceled ones keep access until it, then end.
const SubscriptionRow = ({ sub, onCancel }) => {
  const cancellable = sub.status === 'active' || sub.status === 'past_due';
  const dateLabel = sub.status === 'active' || sub.status === 'past_due' ? 'Renews' : 'Ends';
  return (
    <Table.Tr>
      <Table.Td>
        <Anchor component={Link} to={`/princess/${sub.seller.id}`} size="sm">
          {sub.seller.display_name}
        </Anchor>
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={600}>{formatCents(sub.price_cents)}/mo</Text>
      </Table.Td>
      <Table.Td>
        <StatusBadge sub={sub} />
      </Table.Td>
      <Table.Td>
        {sub.current_period_end ? (
          <Text size="sm">{dateLabel} {formatDate(sub.current_period_end)}</Text>
        ) : (
          <Text size="sm" c="dimmed">—</Text>
        )}
      </Table.Td>
      <Table.Td>
        {cancellable && (
          <Button size="xs" variant="light" color="red" onClick={() => onCancel(sub)}>
            Cancel
          </Button>
        )}
      </Table.Td>
    </Table.Tr>
  );
};

// Fan subscriptions the viewer holds (contract §4.19) —
// GET /api/v1/me/fan-subscriptions; cancel via DELETE (access lasts until
// period end, mirroring §4.4 platform-subscription cancel semantics).
const FanSubscriptionsPage = () => {
  const [page, setPage] = useState(1);
  const { isSignedIn } = useAuth();
  const api = useApiClient();

  const { data, loading, error, refetch } = useApi(
    '/api/v1/me/fan-subscriptions',
    { page, limit: PAGE_LIMIT },
    { enabled: !!isSignedIn, query: { placeholderData: (prev) => prev } }
  );
  const subs = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  const [cancelTarget, setCancelTarget] = useState(null);
  const [canceling, setCanceling] = useState(false);

  const handleConfirmCancel = async () => {
    if (!cancelTarget || canceling) return;
    setCanceling(true);
    try {
      await api.del(`/api/v1/me/fan-subscriptions/${cancelTarget.id}`);
      notifications.show({ color: 'green', message: 'Subscription canceled' });
      setCancelTarget(null);
      refetch();
    } catch (err) {
      notifications.show({
        color: 'red',
        message: err?.message || 'Could not cancel the subscription',
      });
    } finally {
      setCanceling(false);
    }
  };

  return (
    <ProtectedRoute requireAuth={true}>
      <Container size="lg" py="xl">
        <Stack gap="xl">
          <Group gap="xs" align="center">
            <IconHeartStar size="1.5rem" color="var(--mantine-color-pink-6)" />
            <Title order={1}>My Subscriptions</Title>
          </Group>

          {error && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {error.message}
            </Alert>
          )}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Loader size="lg" />
            </div>
          ) : subs.length === 0 ? (
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack align="center" gap="md" py="xl">
                <IconHeartStar size={48} color="var(--mantine-color-gray-4)" />
                <Text c="dimmed" size="lg">No fan subscriptions yet</Text>
                <Text c="dimmed" size="sm">
                  Subscribe to a princess to unlock her private gallery — your
                  subscriptions will show up here.
                </Text>
                <Button
                  component={Link}
                  to="/princesses"
                  color="grape"
                  variant="light"
                  leftSection={<IconBuildingStore size="1rem" />}
                >
                  Browse Princesses
                </Button>
              </Stack>
            </Card>
          ) : (
            <Card shadow="sm" padding={0} radius="md" withBorder>
              <Table.ScrollContainer minWidth={640}>
                <Table striped highlightOnHover verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Princess</Table.Th>
                      <Table.Th>Price</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Renews / ends</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {subs.map((sub) => (
                      <SubscriptionRow key={sub.id} sub={sub} onCancel={setCancelTarget} />
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Card>
          )}

          {totalPages > 1 && (
            <Group justify="center">
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Group>
          )}
        </Stack>

        <Modal
          opened={!!cancelTarget}
          onClose={() => setCancelTarget(null)}
          title="Cancel subscription?"
          centered
          transitionProps={{ duration: 0 }}
        >
          <Stack gap="md">
            <Text size="sm">
              Cancel your subscription to{' '}
              <Text span fw={600}>{cancelTarget?.seller?.display_name}</Text>?
              You'll keep access to their private gallery until{' '}
              {cancelTarget?.current_period_end
                ? formatDate(cancelTarget.current_period_end)
                : 'the end of the current period'}
              , and it won't renew after that.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                color="gray"
                onClick={() => setCancelTarget(null)}
                disabled={canceling}
              >
                Keep subscription
              </Button>
              <Button color="red" onClick={handleConfirmCancel} loading={canceling}>
                Cancel subscription
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </ProtectedRoute>
  );
};

export default FanSubscriptionsPage;
